import {
  AlertCircle,
  FileText,
  LoaderCircle,
} from 'lucide-react';
import {
  useEffect,
  useMemo,
  useState,
} from 'react';

import { getLegalPageDefinition } from '../../config/legalPages';
import { useLanguage } from '../../contexts/LanguageContext';
import { getPublicLegalPage } from '../../api/legalPageApi';

import '../../styles/LegalPages/LegalPages.css';

// Removes the HTML title tag because the browser title is handled separately.
function getRenderableHtml(content) {
  return String(content || '')
    .replace(/<title\b[^>]*>[\s\S]*?<\/title>/gi, '')
    .trim();
}

export default function PublicLegalPage({ slug }) {
  const { language, t } = useLanguage();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  const definition = useMemo(
    () => getLegalPageDefinition(slug),
    [slug],
  );

  const Icon = definition?.icon || FileText;
  const pageLabel = definition
    ? t(definition.labelKey)
    : t('legalPages.public.page');
  const renderedContent = getRenderableHtml(page?.content);
  const brandName = t('landing.brand.name');

  useEffect(
    // Reloads the stored HTML whenever the interface language changes.
    function loadTranslatedPage() {
      let active = true;

      setLoading(true);
      setFailed(false);

      getPublicLegalPage(slug, language)
        .then((result) => {
          if (active) {
            setPage(result);
          }
        })
        .catch(() => {
          if (active) {
            setPage(null);
            setFailed(true);
          }
        })
        .finally(() => {
          if (active) {
            setLoading(false);
          }
        });

      return () => {
        active = false;
      };
    },
    [language, slug],
  );

  useEffect(
    // Uses the translated title returned by Laravel as the browser title.
    function updateDocumentTitle() {
      const previousTitle = document.title;
      const title = page?.title || pageLabel;

      document.title = `${title} | ${brandName}`;

      return () => {
        document.title = previousTitle;
      };
    },
    [brandName, page?.title, pageLabel],
  );

  return (
    <section className="guest-legal-page">
      <div className="guest-legal-container">
        <div className="guest-legal-shell">
          <header className="guest-legal-toolbar">
            <span className="guest-legal-icon" aria-hidden="true">
              <Icon size={22} />
            </span>

            <div>
              <span>{t('legalPages.public.kicker')}</span>
              <h1>{page?.title || pageLabel}</h1>
            </div>
          </header>

          {loading ? (
            <div className="guest-legal-state" role="status">
              <LoaderCircle className="legal-spin" size={30} />
              <strong>{t('legalPages.public.loading')}</strong>
              <span>{t('legalPages.public.loadingDescription')}</span>
            </div>
          ) : null}

          {!loading && failed ? (
            <div
              className="guest-legal-state guest-legal-state-error"
              role="alert"
            >
              <AlertCircle size={30} />
              <strong>{t('legalPages.public.loadError')}</strong>
              <span>{t('legalPages.public.loadErrorDescription')}</span>
            </div>
          ) : null}

          {!loading && !failed && renderedContent ? (
            <article
              className="guest-legal-content"
              dangerouslySetInnerHTML={{ __html: renderedContent }}
            />
          ) : null}

          {!loading && !failed && !renderedContent ? (
            <div className="guest-legal-state">
              <FileText size={30} />
              <strong>{page?.title || pageLabel}</strong>
              <span>{t('legalPages.public.empty')}</span>
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}
